<template>
  <div class="agenda-manager">
    <div class="agenda-header flex items-center justify-between mb-4">
      <div class="title">
        <span class="text-lg font-medium">议程管理</span>
        <!-- <span class="text-gray-500 text-sm ml-2" v-if="venueName">{{ venueName }}</span> -->
      </div>
      <div class="actions">
        <el-button type="primary" size="small" @click="openAddAgendaDialog">
          <i class="el-icon-plus mr-1"></i> 添加议程
        </el-button>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper p-10 text-center">
      <i class="el-icon-loading text-2xl"></i>
      <p class="mt-2 text-gray-500">加载中...</p>
    </div>
    
    <!-- 空状态 -->
    <div v-else-if="!agendaList.length" class="empty-state p-10 text-center">
      <el-empty description="暂无议程数据" :image-size="120">
        <el-button type="primary" @click="openAddAgendaDialog">添加议程</el-button>
      </el-empty>
    </div>
    
    <!-- 议程列表 -->
    <div v-else class="agenda-list">
      <el-table
        :data="agendaList"
        style="width: 100%"
        :max-height="520"
        border
        stripe
      >
        <el-table-column 
          label="时间" 
          width="180" 
          align="center"
        >
          <template slot-scope="scope">
            <div class="time-range">
              <div class="date">{{ scheduleDate || formatDate(scope.row.date) }}</div>
              <div class="time">{{ formatTimeRange(scope.row.startTime, scope.row.endTime) }}</div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column 
          label="标题" 
          width="180"
        >
          <template slot-scope="scope">
            <div class="agenda-title-col">
              <div class="title">{{ scope.row.title }}</div>
              <div class="subtitle" v-if="scope.row.subtitle">{{ scope.row.subtitle }}</div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column 
          label="简介"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <div v-if="scope.row.description" v-html="scope.row.description"></div>
            <span v-else class="text-gray-400">暂无简介</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          label="嘉宾信息" 
          width="280"
        >
          <template slot-scope="scope">
            <div v-if="scope.row.guests && scope.row.guests.length > 0" class="agenda-guests">
              <div v-for="(guest, index) in scope.row.guests" :key="guest.guestId" class="guest-item">
                <span class="guest-role" v-if="guest.role">{{ guest.role }}：</span>
                <span class="guest-name">{{ guest.guestInfo?.name || '未知嘉宾' }}</span>
                <span v-if="index < scope.row.guests.length - 1">、</span>
              </div>
            </div>
            <span v-else class="text-gray-400">暂无嘉宾</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          label="排序" 
          width="70" 
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.sortOrder || 0 }}
          </template>
        </el-table-column>
        
        <el-table-column 
          label="操作" 
          width="150" 
          align="center"
        >
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="primary"
              @click.stop="handleEditAgenda(scope.row)"
            >编辑</el-button>
            <el-button
              size="mini"
              type="danger"
              @click.stop="handleDeleteAgenda(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="agenda-pagination mt-4 flex justify-end">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-size.sync="pageSize"
          :current-page.sync="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
    
    <!-- 添加/编辑议程对话框 -->
    <el-dialog
      :title="agendaForm._id ? '编辑议程' : '添加议程'"
      :visible.sync="agendaDialogVisible"
      width="750px"
      @close="resetAgendaForm"
    >
      <el-form
        ref="agendaForm"
        :model="agendaForm"
        :rules="agendaRules"
        label-width="100px"
        v-loading="formLoading"
      >
        <!-- 基本信息 -->
        <el-card shadow="never" class="mb-4">
          <div slot="header" class="card-header">
            <span>基本信息</span>
          </div>
          
          <el-form-item label="议程标题" prop="title">
            <el-input v-model="agendaForm.title" placeholder="请输入议程标题"></el-input>
          </el-form-item>
          
          <el-form-item label="副标题" prop="subtitle">
            <el-input v-model="agendaForm.subtitle" placeholder="请输入副标题"></el-input>
          </el-form-item>
          
          <el-form-item label="会场" prop="venueName">
            <el-input v-model="venueName" disabled></el-input>
          </el-form-item>
          
          <el-form-item label="时间范围" prop="timeRange">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <el-input
                v-model="startTimeInput"
                placeholder="HH:mm"
                maxlength="5"
                style="width: 120px;"
                @input="handleStartTimeInput"
                @blur="handleTimeBlur"
              />
              <span>至</span>
              <el-input
                v-model="endTimeInput"
                placeholder="HH:mm"
                maxlength="5"
                style="width: 120px;"
                @input="handleEndTimeInput"
                @blur="handleTimeBlur"
              />
            </div>
            <div class="text-gray-500 text-xs">
              日程时间限制: {{ formatTimeRange(scheduleTimeRange?.startTime, scheduleTimeRange?.endTime) || '全天' }}
            </div>
          </el-form-item>
          
          <el-form-item label="排序" prop="sortOrder">
            <el-input-number
              v-model="agendaForm.sortOrder"
              :min="0"
              :max="1000"
              :step="1"
              controls-position="right"
              style="width: 200px"
            ></el-input-number>
            <div class="text-gray-500 text-xs mt-1">
              数字越大，排序越靠前
            </div>
          </el-form-item>
        </el-card>
        
        <!-- 嘉宾信息 -->
        <el-card shadow="never" class="mb-4">
          <div slot="header" class="card-header">
            <span>嘉宾信息</span>
            <el-button 
              type="text" 
              icon="el-icon-plus" 
              @click="showGuestSelector"
            >
              添加嘉宾
            </el-button>
          </div>
          
          <div v-if="agendaForm.guests && agendaForm.guests.length === 0" class="empty-block">
            <el-empty description="暂无嘉宾，请点击添加" :image-size="80"></el-empty>
          </div>
          
          <el-table
            v-else
            :data="agendaForm.guests"
            style="width: 100%"
          >
            <el-table-column label="序号" width="60" align="center">
              <template slot-scope="scope">
                {{ scope.$index + 1 }}
              </template>
            </el-table-column>
            
            <el-table-column label="头像" width="80" align="center">
              <template slot-scope="scope">
                <el-avatar 
                  :size="40" 
                  :src="scope.row.guestInfo?.avatar || ''" 
                  icon="el-icon-user"
                ></el-avatar>
              </template>
            </el-table-column>
            
            <el-table-column prop="guestInfo.name" label="姓名" />
            
            <el-table-column label="角色" width="180">
              <template slot-scope="scope">
                <el-input 
                  v-model="scope.row.role" 
                  placeholder="请输入嘉宾角色"
                  size="small"
                />
              </template>
            </el-table-column>
            
            <el-table-column label="排序" width="100">
              <template slot-scope="scope">
                <el-input-number 
                  v-model="scope.row.sortOrder" 
                  :min="0" 
                  :max="100"
                  size="small"
                  controls-position="right"
                />
              </template>
            </el-table-column>
            
            <el-table-column label="操作" width="80" align="center">
              <template slot-scope="scope">
                <el-button 
                  type="danger" 
                  icon="el-icon-delete" 
                  size="mini" 
                  circle
                  @click="removeGuest(scope.$index)"
                ></el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
        
        <!-- 展示配置 -->
        <el-card shadow="never" class="mb-4">
          <div slot="header" class="card-header">
            <span>展示配置</span>
          </div>
          
          <el-form-item label="跳转按钮" prop="buttonText">
            <el-row :gutter="10">
              <el-col :span="8">
                <el-input 
                  v-model="agendaForm.buttonText" 
                  placeholder="按钮文字，例如：了解更多"
                ></el-input>
              </el-col>
              <el-col :span="16">
                <el-input 
                  v-model="agendaForm.buttonLink" 
                  placeholder="跳转链接，例如：https://example.com"
                ></el-input>
              </el-col>
            </el-row>
          </el-form-item>
          
          <el-form-item label="简介描述" prop="description">
            <div class="editor-container">
              <mc-rich-text
                v-model="agendaForm.description"
                :height="300"
                placeholder="请输入简介描述"
              />
            </div>
            <div class="text-gray-500 text-xs mt-1">
              支持富文本编辑，会在列表中显示
            </div>
          </el-form-item>
        </el-card>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="agendaDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveAgenda" :loading="submitLoading">保存</el-button>
      </div>
    </el-dialog>
    
    <!-- 嘉宾选择器对话框 -->
    <el-dialog
      title="选择嘉宾"
      :visible.sync="guestSelectorVisible"
      width="650px"
      append-to-body
      @open="clearGuestSelection"
      @close="handleGuestDialogClose"
    >
      <div class="guest-selector">
        <div class="mb-4 flex items-center">
          <el-input
            v-model="guestSearchKeyword"
            placeholder="搜索嘉宾姓名"
            clearable
            prefix-icon="el-icon-search"
            style="width: 250px;"
            @keyup.enter.native="searchGuests"
          ></el-input>
          <el-button type="primary" plain @click="searchGuests" class="ml-2">搜索</el-button>
        </div>
        
        <el-table
          ref="guestTable"
          :data="availableGuests"
          style="width: 100%"
          @selection-change="handleGuestSelectionChange"
          v-loading="guestLoading"
          max-height="350"
          row-key="_id"
        >
          <el-table-column type="selection" width="55" :selectable="isGuestSelectable"></el-table-column>
          
          <el-table-column label="头像" width="80" align="center">
            <template slot-scope="scope">
              <el-avatar :size="40" :src="scope.row.avatar" icon="el-icon-user"></el-avatar>
            </template>
          </el-table-column>
          
          <el-table-column prop="name" label="姓名"></el-table-column>
          
          <el-table-column prop="position" label="职位"></el-table-column>
        </el-table>
        
        <div class="mt-4 flex justify-between items-center">
          <div>
            <span class="text-gray-500">已选择 {{ selectedGuests.length }} 位嘉宾</span>
          </div>
          
          <el-pagination
            background
            layout="prev, pager, next"
            :total="guestTotal"
            :page-size="guestPageSize"
            :current-page.sync="guestCurrentPage"
            @current-change="getGuestList"
          ></el-pagination>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="guestSelectorVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmGuestSelection">确定</el-button>
      </div>
    </el-dialog>
    
    <!-- 议程详情浮层 -->
    <el-dialog
      title="议程详情"
      :visible.sync="agendaDetailVisible"
      width="600px"
      class="agenda-detail-dialog"
    >
      <div v-if="selectedAgenda" class="agenda-detail-content">
        <div class="agenda-detail-header">
          <div class="time-block py-2 px-4 text-white text-center rounded mb-4"
               :style="{ backgroundColor: getTimeColor(agendaList.findIndex(a => a._id === selectedAgenda._id)) }">
            {{ formatTimeRange(selectedAgenda.startTime, selectedAgenda.endTime) }}
          </div>
          
          <h2 class="text-xl font-bold mb-2">{{ selectedAgenda.title }}</h2>
          <p v-if="selectedAgenda.subtitle" class="text-base text-gray-600 mb-4">{{ selectedAgenda.subtitle }}</p>
        </div>
        
        <!-- 描述内容 -->
        <div v-if="selectedAgenda.description" class="agenda-detail-description mb-4">
          <h3 class="text-lg font-medium mb-3">描述</h3>
          <div class="description-content p-3 bg-gray-50 border rounded" v-html="selectedAgenda.description"></div>
        </div>
        
        <el-divider></el-divider>
        
        <!-- 嘉宾列表 -->
        <div v-if="selectedAgenda.guests && selectedAgenda.guests.length > 0" class="agenda-detail-guests mb-4">
          <h3 class="text-lg font-medium mb-3">嘉宾</h3>
          <div class="guests-grid">
            <div v-for="guest in selectedAgenda.guests" :key="guest.guestId" class="guest-card p-3 rounded">
              <div class="flex items-center">
                <el-avatar :size="40" :src="guest.guestInfo?.avatar || ''" icon="el-icon-user"></el-avatar>
                <div class="ml-3">
                  <div class="font-medium">{{ guest.guestInfo?.name || '未知嘉宾' }}</div>
                  <div class="text-gray-500 text-sm">{{ guest.role || '无角色' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 按钮链接 -->
        <div v-if="selectedAgenda.buttonText && selectedAgenda.buttonLink" class="agenda-detail-button mt-4 text-center">
          <el-button type="primary" @click="handleButtonClick(selectedAgenda.buttonLink)">
            {{ selectedAgenda.buttonText }}
          </el-button>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="agendaDetailVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEditAgenda(selectedAgenda)">编辑</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getAgendaList, 
  getAgendaDetail, 
  createAgenda, 
  updateAgenda, 
  deleteAgenda,
  checkAgendaTimeConflict
} from '@/api/schedule/agenda';
import { getGuestList } from '@/api/schedule/guest';
import { formatDate } from '@/utils/date';
import McRichText from '@/custom-schema-template/components/McRichText';

export default {
  name: 'AgendaManager',
  
  components: {
    McRichText
  },
  
  props: {
    // 日程ID
    scheduleId: {
      type: String,
      required: true
    },
    
    // 日程时间范围，用于限制议程可选时间
    scheduleTimeRange: {
      type: Object,
      default: () => ({
        startTime: '08:00',
        endTime: '22:00'
      })
    },
    
    // 会场名称
    venueName: {
      type: String,
      default: ''
    },
    
    // 日程日期
    scheduleDate: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      // 时间输入相关
      startTimeInput: '',
      endTimeInput: '',
      
      // 加载状态
      loading: false,
      formLoading: false,
      submitLoading: false,
      
      // 议程列表
      agendaList: [],
      // 分页
      total: 0,
      currentPage: 1,
      pageSize: 10,
      
      // 议程表单对话框可见性
      agendaDialogVisible: false,
      
      // 议程详情浮层可见性
      agendaDetailVisible: false,
      
      // 当前选中的议程
      selectedAgenda: null,
      
      // 议程表单数据
      agendaForm: {
        _id: '',
        scheduleItemId: '',
        title: '',
        subtitle: '',
        timeRange: null, // ['HH:mm','HH:mm']
        startTime: '',
        endTime: '',
        sortOrder: 0,
        guests: [],
        buttonText: '',
        buttonLink: '',
        description: ''
      },
      
      // 表单验证规则
      agendaRules: {
        title: [
          { required: true, message: '请输入议程标题', trigger: 'blur' }
        ],
        timeRange: [
          { required: true, message: '请选择时间范围', trigger: 'change' }
        ]
      },
      
      // 嘉宾选择器
      guestSelectorVisible: false,
      guestLoading: false,
      guestSearchKeyword: '',
      availableGuests: [],
      selectedGuests: [],
      guestTotal: 0,
      guestCurrentPage: 1,
      guestPageSize: 10,
      
      // 颜色列表用于议程时间显示
      timeColors: [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#8E44AD', '#16A085', '#2980B9', '#D35400', '#27AE60'
      ]
    };
  },
  
  created() {
    this.agendaForm.scheduleItemId = this.scheduleId;
    this.fetchAgendaList();
  },
  
  watch: {
    // 监听嘉宾列表变化，重置勾选状态
    availableGuests() {
      this.$nextTick(() => {
        this.clearGuestSelection();
      });
    }
  },
  
  methods: {
    // 获取议程列表
    async fetchAgendaList() {
      if (!this.scheduleId) {
        console.error('未提供日程ID');
        return;
      }
      
      this.loading = true;
      
      try {
        const params = {
          scheduleItemId: this.scheduleId,
          page: this.currentPage,
          pageSize: this.pageSize,
          sortBy: 'startTime'
        };
        const response = await getAgendaList(params);
        
        if (response.status === '10000' || response.code === 200) {
          this.agendaList = response.data || [];
          this.total = response.pagination?.total || 0;
        } else {
          this.$message.error(response.message || '获取议程列表失败');
        }
      } catch (error) {
        console.error('获取议程列表出错:', error);
        this.$message.error('获取议程列表失败: ' + (error.message || '未知错误'));
      } finally {
        this.loading = false;
      }
    },

    // 分页切换
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchAgendaList();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchAgendaList();
    },
    
    // 处理议程项点击
    async handleAgendaItemClick(agenda) {
      this.selectedAgenda = null;
      
      try {
        // 获取最新的议程数据
        const response = await getAgendaDetail({ id: agenda._id });
        
        if (response.status === '10000' || response.code === 200) {
          this.selectedAgenda = response.data || agenda;
          this.agendaDetailVisible = true;
        } else {
          this.$message.error(response.message || '获取议程详情失败');
        }
      } catch (error) {
        console.error('获取议程详情出错:', error);
        this.$message.error('获取议程详情失败: ' + (error.message || '未知错误'));
        
        // 如果获取详情失败，使用列表中的数据
        this.selectedAgenda = agenda;
        this.agendaDetailVisible = true;
      }
    },
    
    // 处理按钮链接点击
    handleButtonClick(link) {
      if (!link) return;
      
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.open(link, '_blank');
      } else {
        // 内部路由或其他处理
        this.$message.info('正在跳转到: ' + link);
      }
    },
    
    // 打开添加议程对话框
    openAddAgendaDialog() {
      this.resetAgendaForm();
      this.agendaDialogVisible = true;
    },
    
    // 重置议程表单
    resetAgendaForm() {
      this.agendaForm = {
        _id: '',
        scheduleItemId: this.scheduleId,
        title: '',
        subtitle: '',
        timeRange: null,
        startTime: '',
        endTime: '',
        sortOrder: 0,
        guests: [],
        buttonText: '',
        buttonLink: '',
        description: ''
      };
      
      // 重置时间输入框
      this.startTimeInput = '';
      this.endTimeInput = '';
      
      // 如果表单引用存在，重置验证
      if (this.$refs.agendaForm) {
        this.$refs.agendaForm.clearValidate();
      }
    },
    
    // 查看议程详情
    handleViewAgenda(agenda) {
      // 在当前页面显示议程详情，或者跳转到详情页
      this.handleEditAgenda(agenda);
    },
    
    // 编辑议程
    async handleEditAgenda(agenda) {
      this.formLoading = true;
      
      try {
        // 获取最新的议程数据
        const response = await getAgendaDetail({ id: agenda._id });
        
        if (response.status === '10000' || response.code === 200) {
          const agendaData = response.data || {};
          
          // 设置表单数据
          this.agendaForm = {
            _id: agendaData._id,
            scheduleItemId: this.scheduleId,
            title: agendaData.title || '',
            subtitle: agendaData.subtitle || '',
            startTime: agendaData.startTime || '',
            endTime: agendaData.endTime || '',
            sortOrder: agendaData.sortOrder || 0,
            buttonText: agendaData.buttonText || '',
            buttonLink: agendaData.buttonLink || '',
            description: agendaData.description || '',
            guests: []
          };
          
          // 设置时间范围（字符串）
          if (agendaData.startTime && agendaData.endTime) {
            this.startTimeInput = agendaData.startTime.substring(0,5);
            this.endTimeInput = agendaData.endTime.substring(0,5);
            this.agendaForm.timeRange = [this.startTimeInput, this.endTimeInput];
          }
          
          // 处理嘉宾数据
          if (agendaData.guests && Array.isArray(agendaData.guests) && agendaData.guests.length > 0) {
            // 确保每个嘉宾都有完整的信息结构
            this.agendaForm.guests = agendaData.guests.map(guest => {
              // 兼容不同的数据结构
              const guestInfo = guest.guestInfo || {};
              
              return {
                guestId: guest.guestId,
                role: guest.role || '',
                sortOrder: guest.sortOrder || 0,
                guestInfo: {
                  _id: guestInfo._id || guest.guestId,
                  name: guestInfo.name || '未知嘉宾',
                  avatar: guestInfo.avatar || '',
                  position: guestInfo.position || '',
                  company: guestInfo.company || ''
                }
              };
            });
          }
          
          this.agendaDialogVisible = true;
        } else {
          this.$message.error(response.message || '获取议程详情失败');
        }
      } catch (error) {
        console.error('获取议程详情出错:', error);
        this.$message.error('获取议程详情失败: ' + (error.message || '未知错误'));
      } finally {
        this.formLoading = false;
      }
    },
    
    // 删除议程
    handleDeleteAgenda(agenda) {
      this.$confirm('确定要删除此议程吗？删除后将无法恢复', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteAgenda({ id: agenda._id });
          
          if (response.status === '10000' || response.code === 200) {
            this.$message.success('删除成功');
            this.fetchAgendaList();
          } else {
            this.$message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除议程出错:', error);
          this.$message.error('删除议程失败: ' + (error.message || '未知错误'));
        }
      }).catch(() => {
        // 取消删除，不做处理
      });
    },
    
    // 保存议程
    saveAgenda() {
      this.$refs.agendaForm.validate(async valid => {
        if (!valid) {
          return false;
        }
        
        // 设置开始和结束时间（已为'HH:mm'字符串）
        if (this.agendaForm.timeRange && this.agendaForm.timeRange.length === 2) {
          this.agendaForm.startTime = this.agendaForm.timeRange[0];
          this.agendaForm.endTime = this.agendaForm.timeRange[1];
        } else {
          this.$message.error('请选择时间范围');
          return false;
        }
        
        // 检查时间是否在日程范围内
        if (this.scheduleTimeRange) {
          const { startTime, endTime } = this.agendaForm;
          const scheduleStart = this.scheduleTimeRange.startTime;
          const scheduleEnd = this.scheduleTimeRange.endTime;
          
          if (startTime < scheduleStart || endTime > scheduleEnd) {
            // this.$message.error(`议程时间必须在日程时间范围内: ${scheduleStart} - ${scheduleEnd}`);
            // return false;
          }
        }
        
        // 检查时间冲突
        try {
          const conflictParams = {
            scheduleItemId: this.scheduleId,
            startTime: this.agendaForm.startTime,
            endTime: this.agendaForm.endTime
          };
          
          // 编辑模式需要排除当前议程
          if (this.agendaForm._id) {
            conflictParams.excludeId = this.agendaForm._id;
          }
          
          const conflictResponse = await checkAgendaTimeConflict(conflictParams);
          
          // if (conflictResponse.status === '10000' || conflictResponse.code === 200) {
          //   const hasConflict = conflictResponse.data?.hasConflict || false;
            
          //   if (hasConflict) {
          //     const conflictInfo = conflictResponse.data?.conflictInfo || {};
          //     this.$message.error(`时间冲突: 与议程 "${conflictInfo.title}" (${conflictInfo.startTime} - ${conflictInfo.endTime}) 冲突`);
          //     return false;
          //   }
          // }
        } catch (error) {
          console.error('检查时间冲突出错:', error);
          // 忽略错误，继续保存
        }
        
        this.submitLoading = true;
        
        try {
          // 准备保存的数据
          const saveData = {
            scheduleItemId: this.scheduleId,
            title: this.agendaForm.title,
            subtitle: this.agendaForm.subtitle,
            startTime: this.agendaForm.startTime,
            endTime: this.agendaForm.endTime,
            sortOrder: this.agendaForm.sortOrder,
            buttonText: this.agendaForm.buttonText,
            buttonLink: this.agendaForm.buttonLink,
            description: this.agendaForm.description,
            guests: this.agendaForm.guests.map(guest => ({
              guestId: guest.guestId,
              role: guest.role,
              sortOrder: guest.sortOrder
            }))
          };
          
          let response;
          
          if (this.agendaForm._id) {
            // 更新议程
            saveData.id = this.agendaForm._id;
            response = await updateAgenda(saveData);
          } else {
            // 创建议程
            response = await createAgenda(saveData);
          }
          
          if (response.status === '10000' || response.code === 200) {
            this.$message.success(this.agendaForm._id ? '更新成功' : '创建成功');
            this.agendaDialogVisible = false;
            this.fetchAgendaList();
          } else {
            this.$message.error(response.message || (this.agendaForm._id ? '更新失败' : '创建失败'));
          }
        } catch (error) {
          console.error('保存议程出错:', error);
          this.$message.error('保存议程失败: ' + (error.message || '未知错误'));
        } finally {
          this.submitLoading = false;
        }
      });
    },
    
    // 验证时间范围
    validateTimeRange() {
      if (!this.agendaForm.timeRange || this.agendaForm.timeRange.length !== 2) {
        return false;
      }
      const startTime = this.agendaForm.timeRange[0];
      const endTime = this.agendaForm.timeRange[1];
      if (!startTime || !endTime) return false;
      if (startTime >= endTime) {
        this.$message.error('结束时间必须晚于开始时间');
        return false;
      }
      return true;
    },
    
    // 已不再需要将Date转为字符串，保留兼容（若传入Date则仍能转换）
    formatTime(date) {
      if (!date) return '';
      if (typeof date === 'string') return date.substring(0,5);
      try {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      } catch (e) {
        return '';
      }
    },
    
    // 格式化时间输入（支持不完整输入自动补零）
    formatTimeInput(input) {
      if (!input) return '';
      const cleaned = input.replace(/[^\d:]/g, '');
      const parts = cleaned.split(':');
      
      if (parts.length === 1) {
        // 只有小时部分
        const hour = parseInt(parts[0]) || 0;
        if (hour > 23) return '';
        return `${hour.toString().padStart(2, '0')}:00`;
      } else if (parts.length >= 2) {
        // 有小时和分钟部分
        const hour = parseInt(parts[0]) || 0;
        const minute = parseInt(parts[1]) || 0;
        if (hour > 23 || minute > 59) return '';
        return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      }
      
      return '';
    },
    
    // 处理开始时间输入
    handleStartTimeInput(value) {
      this.startTimeInput = value;
      this.updateTimeRange();
    },
    
    // 处理结束时间输入
    handleEndTimeInput(value) {
      this.endTimeInput = value;
      this.updateTimeRange();
    },
    
    // 更新timeRange以满足表单验证
    updateTimeRange() {
      const start = this.formatTimeInput(this.startTimeInput);
      const end = this.formatTimeInput(this.endTimeInput);
      
      if (start && end) {
        this.agendaForm.timeRange = [start, end];
      } else if (start || end) {
        this.agendaForm.timeRange = [start || '', end || ''];
      } else {
        this.agendaForm.timeRange = null;
      }
    },
    
    // 失焦时格式化时间
    handleTimeBlur() {
      this.startTimeInput = this.formatTimeInput(this.startTimeInput);
      this.endTimeInput = this.formatTimeInput(this.endTimeInput);
      this.updateTimeRange();
      
      // 检查时间逻辑
      if (this.startTimeInput && this.endTimeInput && this.startTimeInput >= this.endTimeInput) {
        this.$message.warning('开始时间需早于结束时间');
      }
      
      // 检查是否在日程时间范围内
      if (this.scheduleTimeRange && this.startTimeInput && this.endTimeInput) {
        const scheduleStart = this.scheduleTimeRange.startTime;
        const scheduleEnd = this.scheduleTimeRange.endTime;
        
        if (this.startTimeInput < scheduleStart || this.endTimeInput > scheduleEnd) {
          // this.$message.warning(`议程时间必须在日程时间范围内: ${scheduleStart} - ${scheduleEnd}`);
        }
      }
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      
      try {
        if (typeof date === 'string') {
          // 尝试直接返回日期部分
          if (date.includes('T')) {
            return date.split('T')[0];
          }
          return date;
        }
        
        // 如果是Date对象
        if (date instanceof Date) {
          return formatDate(date, 'YYYY-MM-DD');
        }
        
        return date;
      } catch (e) {
        console.error('格式化日期错误:', e);
        return date;
      }
    },
    
    // 格式化时间范围
    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return '';
      return `${startTime} - ${endTime}`;
    },
    
    // 根据索引获取时间颜色
    getTimeColor(index) {
      const colorIndex = index % this.timeColors.length;
      return this.timeColors[colorIndex];
    },
    
    // 显示嘉宾选择器
    async showGuestSelector() {
      // 重置所有状态
      this.selectedGuests = [];
      this.guestCurrentPage = 1;
      this.guestSearchKeyword = '';
      
      try {
        // 先加载数据
        await this.getGuestList();
        
        // 显示对话框
        this.guestSelectorVisible = true;
        
        // 确保DOM已更新
        await this.$nextTick();
        
        // 清空选择
        if (this.$refs.guestTable) {
          this.$refs.guestTable.clearSelection();
        }
      } catch (error) {
        console.error('打开嘉宾选择器出错:', error);
        this.$message.error('加载嘉宾列表失败');
      }
    },
    
    // 获取嘉宾列表
    async getGuestList() {
      this.guestLoading = true;
      
      try {
        const params = {
          page: this.guestCurrentPage,
          pageSize: this.guestPageSize,
          search: this.guestSearchKeyword
        };
        
        const response = await getGuestList(params);
        console.log('Guest List Response:', response);
        
        if (response.status === '10000' || response.code === 200) {
          // 确保数据有唯一ID
          this.availableGuests = (response.data || []).map(guest => {
            // 确保每个嘉宾都有唯一ID作为key
            return {
              ...guest,
              _id: guest._id || guest.id || Date.now() + Math.random().toString(36).substring(2, 15)
            };
          });
          
          this.guestTotal = response.pagination?.total || this.availableGuests.length || 0;
          
          console.log('加载到嘉宾数量:', this.availableGuests.length);
          return true;
        } else {
          this.$message.error(response.message || '获取嘉宾列表失败');
          return false;
        }
      } catch (error) {
        console.error('获取嘉宾列表出错:', error);
        this.$message.error('获取嘉宾列表失败: ' + (error.message || '未知错误'));
        return false;
      } finally {
        this.guestLoading = false;
      }
    },
    
    // 搜索嘉宾
    searchGuests() {
      this.guestCurrentPage = 1;
      this.selectedGuests = []; // 重置选中状态
      this.getGuestList().then(() => {
        // 搜索后重置勾选状态
        if (this.$refs.guestTable) {
          this.$refs.guestTable.clearSelection();
        }
      });
    },
    
    // 处理嘉宾选择变更
    handleGuestSelectionChange(selection) {
      console.log('嘉宾选择变更, 选中数量:', selection.length);
      
      // 使用深拷贝避免Vue的响应式问题
      this.selectedGuests = JSON.parse(JSON.stringify(selection));
      
      // 添加详细日志
      if (this.selectedGuests.length > 0) {
        console.log('已选嘉宾:', this.selectedGuests.map(g => g.name).join(', '));
      } else {
        console.log('没有选中任何嘉宾');
      }
    },
    
    // 确认嘉宾选择
    confirmGuestSelection() {
      console.log('确认嘉宾选择, 已选嘉宾数量:', this.selectedGuests.length);
      
      if (this.selectedGuests.length === 0) {
        this.$message.warning('请至少选择一名嘉宾');
        return;
      }

      // 获取当前已选择的嘉宾ID，用于检查重复
      const existingGuestIds = this.agendaForm.guests.map(g => g.guestId);
      console.log('现有嘉宾ID:', existingGuestIds);
      
      // 添加新选择的嘉宾
      let addedCount = 0;
      
      this.selectedGuests.forEach(guest => {
        const guestId = guest._id || guest.id;
        console.log('处理嘉宾:', guest.name, 'ID:', guestId);
        
        // 避免重复添加
        if (!existingGuestIds.includes(guestId)) {
          const newGuest = {
            guestId: guestId,
            role: guest.position || '',
            sortOrder: this.agendaForm.guests.length,
            guestInfo: {
              _id: guestId,
              name: guest.name,
              avatar: guest.avatar,
              title: guest.position || guest.title || '',
              position: guest.position || '',
              company: guest.company || ''
            }
          };
          
          console.log('添加新嘉宾:', newGuest.guestInfo.name);
          this.agendaForm.guests.push(newGuest);
          addedCount++;
        } else {
          console.log('嘉宾已存在，跳过:', guest.name);
        }
      });
      
      console.log('更新后的嘉宾列表:', this.agendaForm.guests);
      
      if (addedCount > 0) {
        this.$message.success(`成功添加 ${addedCount} 名嘉宾`);
      } else {
        this.$message.info('未添加任何嘉宾，可能是已经存在于列表中');
      }
      
      this.guestSelectorVisible = false;
    },
    
    // 移除嘉宾
    removeGuest(index) {
      const removedGuest = this.agendaForm.guests[index];
      this.agendaForm.guests.splice(index, 1);
      
      // 添加提示
      this.$message.success(`已移除嘉宾: ${removedGuest.guestInfo?.name || '未知嘉宾'}`);
    },
    
    // 检查嘉宾是否可选
    isGuestSelectable(row) {
      // 如果嘉宾已在列表中，可以决定是否允许再次选择
      if (this.agendaForm.guests && this.agendaForm.guests.length > 0) {
        const rowId = row._id || row.id;
        const exists = this.agendaForm.guests.some(g => {
          const guestId = typeof g.guestId === 'object' ? g.guestId._id || g.guestId.id : g.guestId;
          return guestId === rowId;
        });
        
        // 如果已存在，可以返回false禁止选择，或者返回true允许重复选择
        // 这里我们允许重复选择，但会在确认时跳过已存在的嘉宾
        return true;
      }
      return true;
    },
    
    // 清空嘉宾选择
    clearGuestSelection() {
      console.log('清空嘉宾选择');
      this.selectedGuests = [];
      // 确保DOM已更新后再清空选择
      this.$nextTick(() => {
        if (this.$refs.guestTable) {
          this.$refs.guestTable.clearSelection();
        }
      });
    },
    
    // 处理嘉宾对话框关闭
    handleGuestDialogClose() {
      console.log('嘉宾对话框关闭');
      this.selectedGuests = [];
      this.guestCurrentPage = 1;
      this.guestSearchKeyword = '';
    }
  }
};
</script>

<style lang="scss" scoped>
.agenda-manager {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  
  .agenda-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    position: relative;
    
    .title {
      position: relative;
      
      .text-lg {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        position: relative;
        padding-left: 12px;
        
        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 18px;
          background-color: #409EFF;
          border-radius: 2px;
        }
      }
    }
  }
  
  .loading-wrapper {
    height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    .el-icon-loading {
      font-size: 32px;
      color: #409EFF;
      margin-bottom: 16px;
    }
  }
  
  .empty-state {
    height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    
    ::v-deep .el-empty__image {
      width: 120px;
      height: 120px;
    }
    
    ::v-deep .el-empty__description {
      margin-top: 16px;
      margin-bottom: 16px;
      color: #909399;
    }
  }
  
  .agenda-list {
    margin-top: 20px;
    
    .time-range {
      .date {
        font-size: 14px;
        color: #606266;
        margin-bottom: 4px;
      }
      
      .time {
        font-size: 14px;
        color: #409EFF;
        font-weight: 500;
      }
    }
    
    .agenda-title-col {
      .title {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 4px;
      }
      
      .subtitle {
        font-size: 12px;
        color: #909399;
      }
    }
    
    .agenda-guests {
      display: flex;
      flex-wrap: wrap;
      
      .guest-item {
        font-size: 13px;
        
        .guest-role {
          color: #909399;
        }
        
        .guest-name {
          color: #606266;
        }
      }
    }
  }
  
  // 对话框样式
  ::v-deep .el-dialog {
    border-radius: 8px;
    overflow: hidden;
    
    .el-dialog__header {
      background-color: #f8f9fb;
      border-bottom: 1px solid #ebeef5;
      padding: 16px 20px;
      
      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }
    }
    
    .el-dialog__body {
      padding: 20px;
    }
    
    .el-dialog__footer {
      border-top: 1px solid #ebeef5;
      padding: 12px 20px;
    }
  }
  
  // 表单卡片样式
  ::v-deep .el-card {
    border-radius: 8px;
    margin-bottom: 16px;
    overflow: hidden;
    
    .el-card__header {
      padding: 12px 16px;
      background-color: #f8f9fb;
      
      .card-header {
        font-weight: 600;
        color: #303133;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }
    
    .el-card__body {
      padding: 16px;
    }
    
    .el-form-item {
      margin-bottom: 18px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  
  // 嘉宾选择器
  .guest-selector {
    .guest-table {
      border-radius: 6px;
      overflow: hidden;
      
      ::v-deep .el-table th {
        background-color: #f8f9fb;
      }
    }
  }
  
  // 议程详情对话框
  .agenda-detail-dialog {
    ::v-deep .el-dialog__body {
      padding: 0;
    }
    
    .agenda-detail-content {
      padding: 20px;
    }
    
    .agenda-detail-header {
      text-align: center;
      margin-bottom: 20px;
      
      .time-block {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 16px;
        border-radius: 6px;
        font-weight: 600;
        letter-spacing: 1px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        
        &:before {
          content: '\e78f'; // el-icon-time
          font-family: element-icons !important;
          margin-right: 8px;
          font-size: 16px;
        }
      }
      
      h2 {
        margin: 16px 0 8px;
        font-size: 20px;
        font-weight: 600;
        color: #303133;
      }
    }
    
    ::v-deep .el-divider {
      margin: 16px 0;
    }
    
    .agenda-detail-guests {
      h3 {
        font-size: 16px;
        margin-bottom: 12px;
        color: #303133;
        font-weight: 600;
        
        &:before {
          content: '\e7ae'; // el-icon-user
          font-family: element-icons !important;
          margin-right: 8px;
          color: #409EFF;
        }
      }
      
      .guests-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 12px;
        
        .guest-card {
          background-color: #f8f9fb;
          border-radius: 6px;
          transition: all 0.3s;
          
          &:hover {
            background-color: #ecf5ff;
            transform: translateY(-2px);
          }
          
          .font-medium {
            font-weight: 600;
            color: #303133;
            margin-bottom: 4px;
          }
        }
      }
    }
    
    .agenda-detail-description {
      h3 {
        font-size: 16px;
        margin-bottom: 12px;
        color: #303133;
        font-weight: 600;
        
        &:before {
          content: '\e67a'; // el-icon-document
          font-family: element-icons !important;
          margin-right: 8px;
          color: #409EFF;
        }
      }
      
      .description-content {
        background-color: #f8f9fb;
        border-radius: 6px;
        padding: 16px;
        color: #606266;
        line-height: 1.6;
        font-size: 14px;
        max-height: 200px;
        overflow-y: auto;
      }
    }
    
    .agenda-detail-button {
      margin-top: 24px;
      
      .el-button {
        padding: 9px 20px;
        font-size: 14px;
      }
    }
  }
  
  // 添加嘉宾按钮
  ::v-deep .el-button--text {
    color: #409EFF;
    padding: 0;
    
    &:hover {
      color: #66b1ff;
    }
  }
  
  // 空状态
  .empty-block {
    padding: 30px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  // 响应式
  @media (max-width: 768px) {
    .agenda-manager {
      padding: 16px;
    }
    
    .agenda-item {
      .agenda-header {
        .agenda-title {
          max-width: 180px;
        }
      }
      
      .agenda-button {
        .truncate {
          max-width: 160px;
        }
      }
    }
    
    .agenda-detail-dialog {
      .agenda-detail-header {
        h2 {
          font-size: 18px;
        }
      }
      
      .guests-grid {
        grid-template-columns: 1fr;
      }
    }
  }
}

// 全局样式覆盖
::v-deep .el-table .cell {
  white-space: nowrap;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #f5f7fa;
}

::v-deep .el-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 500;
}

::v-deep .el-button--mini {
  padding: 7px 10px;
  font-size: 12px;
}

.text-gray-400 {
  color: #9ca3af;
}

.text-gray-500 {
  color: #6b7280;
}

.text-gray-600 {
  color: #4b5563;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
</style> 